import { StyleSheet, View } from 'react-native';

import { Button, Text, tokens } from '@fhs/ui';
import Picture from '@fhs-legacy/frontend/src/components/picture';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { CART_REMOVE_ITEM_MODAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import { useCartValues } from '../../../state/cart-store';

type Props = {
  cartId: string;
  onClose(): void;
};

export function CartActionRemoveItem({ cartId, onClose }: Props) {
  const { cartEntries } = useCartValues();
  const { confirmRemoveItemFromCart, getRemoveItemWithOfferText } = useCartContext();

  const item = cartEntries.find(entry => entry.cartId === cartId);

  if (!item) {
    onClose();
    return null;
  }

  const removeItemWithOffer = getRemoveItemWithOfferText(item);

  return (
    <View
      testID={CART_REMOVE_ITEM_MODAL}
      style={styles.modalContainer}
      accessibilityRole="alert"
      accessibilityLabel="Remove item confirmation"
    >
      <Text.Heading type="one" accessibilityRole="header">
        Remove Item
      </Text.Heading>
      <Text.Ui size="md" style={styles.copy}>
        Are you sure you want to remove this item from your cart? {removeItemWithOffer}
      </Text.Ui>

      <View style={styles.itemContainer} accessibilityLabel={`Item to remove: ${item?.name}`}>
        {item?.image?.asset && (
          <Picture
            width="64px"
            height="56px"
            image={{
              __typename: 'Image',
              asset: { __typename: 'SanityImageAsset', ...item.image.asset },
            }}
            alt={`An image of a ${item?.name}`}
            objectFitContain
          />
        )}
        <Text.Ui size="md" weight="semibold" style={styles.itemName} numberOfLines={2}>
          {item?.name}
        </Text.Ui>
      </View>

      <View style={styles.buttonGroup}>
        <Button
          size="lg"
          onPress={() => {
            confirmRemoveItemFromCart(cartId);
            onClose();
          }}
          accessibilityLabel="Confirm removal"
        >
          <Button.Text>Remove</Button.Text>
        </Button>
        <Button size="lg" type="outline" onPress={onClose} accessibilityLabel="Cancel removal">
          <Button.Text>Cancel</Button.Text>
        </Button>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    maxWidth: 334,
    marginHorizontal: 'auto',
    paddingVertical: 33,
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: tokens.colors.$black10,
    minHeight: 80,
    width: '100%',
    marginBottom: 20,
  },
  itemName: {
    flex: 1,
    color: 'black',
    textAlignVertical: 'center',
  },
  copy: {
    flex: 1,
    paddingTop: 12,
    paddingBottom: 24,
    color: 'black',
  },
  buttonGroup: {
    gap: 8,
  },
});
